import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { generateShareLink, copyShareLinkToClipboard, revokeShareLink } from '../utils/shareLinkService';

const ShareLinkCard = ({ dealId, onShareLinkCreated }) => {
  const [shareLink, setShareLink] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Auto-load existing share link if available
    loadExistingShareLink();
  }, [dealId]);

  const loadExistingShareLink = async () => {
    try {
      // This would be called when component mounts to load existing share link
      // For now, we'll just initialize empty state
    } catch (err) {
      console.error('Error loading share link:', err);
    }
  };

  const handleGenerateLink = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const link = await generateShareLink(dealId);
      setShareLink(link);
      if (onShareLinkCreated) {
        onShareLinkCreated(link);
      }
    } catch (err) {
      console.error('Error generating share link:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    const success = await copyShareLinkToClipboard(shareLink.shareUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevokeLink = async () => {
    if (!confirm('Are you sure? The current share link will no longer work.')) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await revokeShareLink(dealId);
      setShareLink(null);
    } catch (err) {
      console.error('Error revoking share link:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleGenerateLink}
        disabled={isLoading}
        className="bg-black hover:bg-gray-800 text-white px-8 py-3 text-lg font-bold rounded flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Icon name="Loader" className="animate-spin" size={18} />
            <span className="ml-2">Generating...</span>
          </>
        ) : (
          <span>Share with Client</span>
        )}
      </button>

      {/* Share Link Details - Show Below Buttons After Click */}
      {shareLink && (
        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6 w-full">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-black mb-3">Share Link Created</h3>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center space-x-3 bg-gray-50 border border-gray-200 rounded p-3">
              <Icon name="Link" size={18} color="#666" />
              <a
                href={shareLink.shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-sm text-black hover:text-[#bb5d10] underline cursor-pointer truncate transition-colors"
                title={shareLink.shareUrl}
              >
                {shareLink.shareUrl}
              </a>
              <button
                onClick={handleCopyLink}
                className="ml-auto p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                title="Copy to clipboard"
              >
                <Icon name={copied ? 'CheckCircle' : 'Copy'} size={18} color={copied ? '#10b981' : '#666'} />
              </button>
            </div>

            {copied && (
              <div className="text-sm text-green-600 flex items-center space-x-1">
                <Icon name="CheckCircle" size={16} />
                <span>Link copied to clipboard!</span>
              </div>
            )}

            <div className="flex items-center space-x-2 text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-3">
              <Icon name="Info" size={16} />
              <span>
                Share this link with your clients. They'll see the contract details without needing to log in.
              </span>
            </div>

            <button
              onClick={handleRevokeLink}
              disabled={isLoading}
              className="w-full text-red-600 hover:text-red-700 border border-red-200 rounded py-2 hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Revoke Share Link
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareLinkCard;
